/**
 * ErrorAlert Component
 *
 * Consistent error display across admin components.
 * Replaces ~100 lines of duplicate error UI code.
 *
 * @example
 * ```tsx
 * <ErrorAlert error={error} onDismiss={() => setError(null)} />
 * ```
 */

import { X } from 'lucide-react';

interface ErrorAlertProps {
  /** Error message to display */
  error: string | null;

  /** Optional dismiss handler */
  onDismiss?: () => void;

  /** Optional title (default: "Error") */
  title?: string;

  /** Variant style */
  variant?: 'danger' | 'warning';
}

export function ErrorAlert({
  error,
  onDismiss,
  title = 'Error',
  variant = 'danger'
}: ErrorAlertProps) {
  if (!error) return null;

  const variantClasses = {
    danger: {
      border: 'border-red-400',
      bg: 'bg-red-50',
      text: 'text-red-700',
      icon: 'text-red-400',
    },
    warning: {
      border: 'border-yellow-400',
      bg: 'bg-yellow-50',
      text: 'text-yellow-700',
      icon: 'text-yellow-400',
    },
  };

  const styles = variantClasses[variant];

  return (
    <div className={`mb-6 border-l-4 ${styles.border} ${styles.bg} p-4`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {title && (
            <h3 className={`mb-1 font-semibold ${styles.text}`}>
              {title}
            </h3>
          )}
          <p className={`text-sm ${styles.text}`}>{error}</p>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`ml-4 ${styles.icon} hover:opacity-70`}
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
